import { useEffect, type CSSProperties } from "react";
import { useHudStore } from "../store/hudStore";
import { computeView } from "../store/selectors";
import { ensureWeather } from "../lib/weather";
import { useStaggeredGrid } from "../lib/useStaggeredGrid";
import TopBar from "../components/TopBar";
import EngineerSprite, { type EngineerVariant } from "../components/EngineerSprite";
import QuestRow from "../components/QuestRow";
import AddQuestForm from "../components/AddQuestForm";
import Footer from "../components/Footer";
import { Badge, Button, Card, GoalCard, Progress, Select, Tabs } from "../design-system/components";

// The HUD home screen (app.html screenHud, lines 642–780). Sections:
// Context and one focus, then actionable quests beside goals and life areas.


export default function Hud() {
  const st = useHudStore();
  const v = computeView(st);
  const workspaceRef = useStaggeredGrid();

  useEffect(() => {
    void ensureWeather(); // real date + Tagaytay weather (upgrades to device location if allowed)
  }, []);

  const browseBoard = () => {
    document.getElementById("quest-board")?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "start" });
  };

  return (
    <div className="page-frame">
      <TopBar v={v} />
      <div className="hud-sections">
        <div className="context-strip">
          <span><i className="fa-regular fa-calendar" aria-hidden="true" />{v.todayLine}</span>
          <span className="context-place"><i className="fa-solid fa-location-dot" aria-hidden="true" />{v.place}</span>
          <span><i className={v.weatherFa} aria-hidden="true" />{v.weatherLine}</span>
          <span className="context-weather-note">{v.weatherNote}</span>
        </div>

        {/* hero */}
        <section className="today-hero" aria-labelledby="today-title">
          <div className="today-copy">
            <div className="today-context">
              <Badge tone="accent"><i aria-hidden="true" className="fa-solid fa-thumbtack" />{v.hasPin ? "Your focus" : "Today"}</Badge>
              <span>{v.hasPin ? v.pinMeta : "One thing at a time"}</span>
            </div>
            <h1 id="today-title">{v.hasPin ? v.pinnedTitle : v.hasOpenQuests ? "Pick your next quest" : "Your board is clear"}</h1>
            <p className="today-note">{v.hasPin ? v.pinnedNote : v.hasOpenQuests ? "Pick one task below. Pin it for today, then give it your focus." : "Add a quest when you're ready to start something new."}</p>
            <div className="today-actions">
              <Button variant="primary" onClick={v.hasPin ? st.startPinned : v.hasOpenQuests ? browseBoard : st.openAdd}>
                {v.hasPin ? v.startLabel : v.hasOpenQuests ? "Browse today’s quests" : "Add a quest"}
                <i aria-hidden="true" className="fa-solid fa-arrow-right" />
              </Button>
              {v.hasPin && <Badge tone="warning" className="today-xp"><i aria-hidden="true" className="fa-solid fa-bolt" />{v.pinnedXpLabel}</Badge>}
              {v.hasPin && <Button onClick={browseBoard}>Change focus</Button>}
            </div>
          </div>
          <div className="today-readout">
            <div className="today-ring">
              <div className="today-ring-glow" />
              <svg viewBox="0 0 186 186" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                <defs>
                  <linearGradient id="todayG" x1="0" y1="1" x2="1" y2="0">
                    <stop offset="0" stopColor="var(--accent-deep)" />
                    <stop offset="1" stopColor="var(--emerald)" />
                  </linearGradient>
                </defs>
                <circle cx="93" cy="93" r="78" fill="none" stroke="var(--raise)" style={{ strokeWidth: 13 }} />
                <circle cx="93" cy="93" r="78" fill="none" stroke="url(#todayG)" transform="rotate(130 93 93)" style={{ strokeWidth: 13, strokeLinecap: "round", strokeDasharray: 490.1, transition: "stroke-dashoffset .9s cubic-bezier(.16,1,.3,1)", strokeDashoffset: v.todayOffset }} />
              </svg>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
                <div className="today-ring-value" style={{ fontFamily: "var(--mono)", fontWeight: 700, letterSpacing: 0, lineHeight: 1 }}>{v.xpToday}</div>
                <div className="today-ring-label" style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--mut)" }}>XP today</div>
              </div>
              {v.burst && (
                <div style={{ position: "absolute", left: "50%", top: "16%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, pointerEvents: "none", animation: "burst 1.4s cubic-bezier(.16,1,.3,1) forwards", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: 21, fontWeight: 700, color: "var(--green-ink)" }}>{v.burstText}</span>
                  {v.burstSecured && (
                    <span style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--orange-ink)" }}>day secured</span>
                  )}
                </div>
              )}
            </div>
            <div className="today-readout-caption"><b>{v.xpToday} earned</b><br />{v.boardXp} XP available</div>
          </div>
        </section>

        <div className="hud-workspace" ref={workspaceRef}>
        {/* board */}
        <Card id="quest-board" className="hud-board" data-hud-lane="primary" role="region" aria-labelledby="board-title" headingId="board-title" title={<>Quests <span className="board-count">{v.openCount}</span></>} actions={<>
          <Button size="compact" icon="fa-solid fa-clock-rotate-left" onClick={st.openLog}>Log</Button>
          <Button size="compact" icon="fa-solid fa-plus" onClick={st.openAdd}>New</Button>
        </>}>
          <div className="board-controls">
            <Tabs className="board-range" label="Quest time range" value={st.range} onChange={(range) => st.setRange(range as typeof st.range)} items={v.ranges.map((r) => ({ id: r.key, label: r.label }))} />
            <div className="domain-select">
              <Select label="Domain" value={st.filter} onChange={(event) => st.setFilter(event.target.value)}>
                {v.filters.map((f) => <option key={f.label} value={f.label}>{f.label === "All" ? "All domains" : f.label}</option>)}
              </Select>
            </div>
            <div className="board-filters" role="group" aria-label="Filter quests by domain">
              {v.filters.map((f) => <Button key={f.label} size="compact" variant={st.filter === f.label ? "primary" : "secondary"} onClick={() => st.setFilter(f.label)} aria-pressed={st.filter === f.label}>{f.label}</Button>)}
            </div>
          </div>
          {st.adding && <AddQuestForm />}
          {v.visibleQuests.map((q) => (
            <QuestRow key={q.id} quest={q} />
          ))}
          {v.hasDone && (
            <div style={{ borderTop: "1px solid var(--line)", marginTop: 4 }}>
              <button type="button" onClick={st.toggleDone} className="ds-focusable" aria-expanded={v.showDone} style={{ minHeight: 44, display: "flex", alignItems: "center", gap: 10, width: "100%", background: "transparent", border: "none", padding: "14px 0", cursor: "pointer" }}>
                <i aria-hidden="true" className={v.doneChevron} style={{ fontSize: 10, lineHeight: 1, color: "var(--mut)", width: 10 }} />
                <i aria-hidden="true" className="fa-solid fa-circle-check" style={{ fontSize: 13, lineHeight: 1, color: "var(--green)" }} />
                <span style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--mut)" }}>{v.doneLabel}</span>
              </button>
              {v.showDone && (
                <div style={{ display: "flex", flexDirection: "column", paddingBottom: 8 }}>
                  {v.doneQuests.map((q) => (
                    <div key={q.id} className="hud-done-row">
                      <span style={{ width: 22, height: 22, flex: "0 0 auto", borderRadius: 8, display: "grid", placeItems: "center", background: "var(--accent)" }}>
                        <i aria-hidden="true" className="fa-solid fa-check" style={{ fontSize: 11, lineHeight: 1, color: "var(--on-accent)" }} />
                      </span>
                      <span className="hud-done-title" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--mut)", textDecoration: "line-through" }}>{q.title}</span>
                      <div className="hud-done-meta">
                      <span className="hud-done-domain" style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)" }}>{q.domain}</span>
                      <Button size="compact" icon="fa-solid fa-rotate-left" onClick={() => st.undo(q.id)} aria-label={`Undo completion of ${q.title}`}>Undo</Button>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--mut)", minWidth: 38, textAlign: "right", flex: "0 0 auto" }}>+{q.xp}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {v.noQuests && (
            <div style={{ padding: "34px 0 30px", textAlign: "center", color: "var(--mut)" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--sub)" }}>{v.emptyTitle}</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 7, textWrap: "pretty" }}>{v.emptyNote}</div>
            </div>
          )}
        </Card>
            <Card className="goals-panel" data-hud-lane="secondary" role="region" aria-labelledby="goals-heading" headingId="goals-heading" title="Main quests" description="Every quest lives under one.">
              <div className="goals-grid" data-mains-grid>
                {v.mains.map((m) => <GoalCard key={m.id} title={m.name} icon={<i className={m.fa} />} progress={parseFloat(m.pct)} label={m.progress} valueLabel={m.pctText} onClick={() => st.openMain(m.id)} style={{ "--ds-tone": m.color } as CSSProperties} />)}
              </div>
            </Card>

        {/* trophy shelf */}
        {v.hasDoneMains && <Card className="trophy-shelf" data-hud-lane="secondary" title={<span className="shelf-heading"><i className="fa-solid fa-trophy" aria-hidden="true" />Shelf <Badge tone="warning">{v.shelfCount}</Badge></span>} actions={(v.hasShelfMore || st.shelfOpen) && <Button variant="quiet" size="compact" icon={v.shelfChevron} onClick={st.toggleShelf} aria-expanded={st.shelfOpen}>{st.shelfOpen ? "Show less" : v.shelfMore}</Button>}>
          <div className="shelf-goals">
            {v.shelfChips.map((tr) => <Button key={tr.id} size="compact" className="shelf-goal" onClick={() => st.openMain(tr.id)} title={`Open ${tr.name}`}>{tr.name}<span>{tr.date}</span></Button>)}
          </div>
        </Card>}

        {/* Life areas follow the goals and the task board in the reading order. */}
        <Card className="domains-panel" data-hud-lane="auto" role="region" aria-labelledby="domains-heading" headingId="domains-heading" title="Domain heat" description="XP earned today">
          <div className="domain-legend">
            {([["var(--green)", "Alive"], ["var(--gold)", "Slipping"], ["var(--danger)", "At Risk"], ["var(--mut)", "Cold"]] as [string, string][]).map(([c, label]) => <span key={label}><b style={{ background: c }} />{label}</span>)}
          </div>
        <div className="domains-grid" data-domain-grid>
          {v.domains.map((d) => (
            <button key={d.name} type="button" className="domain-card ds-focusable" onClick={() => st.openDomain(d.name)}>
              <span className="domain-card-heading" style={{ color: d.color }}>
                <i aria-hidden="true" className={d.icon} />
                <span>{d.name}</span>
              </span>
              {/* The sprite shares the XP row, leaving the title room to wrap. */}
              <span style={{ display: "flex", alignItems: "flex-end", gap: 4, color: d.color }}>
                <b style={{ fontSize: 24, fontWeight: 700, fontFamily: "var(--display)", letterSpacing: "-0.01em", lineHeight: 1 }}>{d.xp}</b>
                <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.75 }}>XP</span>
                <span style={{ width: 19, height: 24, flex: "0 0 auto", display: "block", marginLeft: "auto" }} aria-hidden="true">
                  <EngineerSprite variant={d.sprite as EngineerVariant} />
                </span>
              </span>
              <Progress value={parseFloat(d.health)} aria-label={`${d.name} health`} style={{ "--ds-tone": d.color } as CSSProperties} />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--mut)" }}>{d.last}</span>
            </button>
          ))}
        </div>

        </Card>

        {/* Supporting panels fill the shorter desktop lane without remounting. */}
          <Card className="badges-panel" data-hud-lane="auto" title="Badges" actions={<><Badge>{v.badgeCount}</Badge><Button variant="quiet" size="compact" onClick={st.goCharacter}>See all <i aria-hidden="true" className="fa-solid fa-arrow-right" /></Button></>}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {v.badgeStrip.map((b) => (
                <button key={b.name} type="button" onClick={() => st.openBadge(b.name)} className="ds-focusable" aria-label={`Badge: ${b.name}, ${b.earned ? "earned" : "locked"}`} style={{ width: 44, height: 44, borderRadius: 999, background: b.wellBg, border: `1px solid ${b.wellBd}`, display: "grid", placeItems: "center", cursor: "pointer", padding: 0 }}>
                  <span style={{ position: "relative", width: 23, height: 23, display: "grid", placeItems: "center" }}>
                    <span style={{ position: "absolute", inset: 0, borderRadius: 7, transform: "rotate(45deg)", background: b.gem, boxShadow: b.shadow }} />
                    <i aria-hidden="true" className={b.fa} style={{ position: "relative", fontSize: 10, lineHeight: 1, color: b.iconColor, textShadow: "0 1px 2px rgba(0,0,0,.5)" }} />
                  </span>
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--mut)", marginTop: 16, textWrap: "pretty" }}>{v.badgeHint}</div>
          </Card>
          <Card className="attributes-panel" data-hud-lane="auto" title="Attributes" actions={<Button variant="quiet" size="compact" onClick={st.goCharacter}>Open trees <i aria-hidden="true" className="fa-solid fa-arrow-right" /></Button>}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 12 }}>
              {v.attrs.map((a) => (
                <button key={a.name} type="button" onClick={st.goCharacter} className="ds-focusable" style={{ textAlign: "left", display: "flex", flexDirection: "column", gap: 8, padding: "12px", borderRadius: 10, background: "var(--card2)", border: "1px solid var(--control-edge)", cursor: "pointer" }}>
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)" }}>{a.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "var(--mut)", whiteSpace: "nowrap" }}>Lv {a.level}</span>
                  </span>
                  <Progress value={parseFloat(a.pct)} aria-label={`${a.name} level progress`} style={{ "--ds-tone": a.color } as CSSProperties} />
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
